/**
 * Marca do cliente — o logotipo quando existe, a inicial quando não existe.
 *
 * Vive num componente próprio porque tem DOIS consumidores com a mesma
 * pergunta ("como este cliente se apresenta?") e tamanhos diferentes: a
 * listagem (28px, ao lado do nome) e o formulário (72px, na pré-visualização
 * do cartão de logotipo). Duplicá-lo faria as duas superfícies divergirem no
 * fallback — que é justamente a parte que ninguém repara até estar errada.
 *
 * O quadrado do fallback NÃO é redondo de propósito: um avatar redondo é
 * gramática de PESSOA (é o que `AvatarGradient` faz nos profissionais). Um
 * cliente é uma empresa, e a marca de uma empresa lê-se num selo quadrado.
 */
import type { CSSProperties, ReactElement } from 'react'

import { cn } from '@/lib/utils'

export interface CustomerLogoProps {
  name: string
  /** URL do binário (`customerLogoUrl`) ou do ficheiro pendente. `null` = sem logotipo. */
  src: string | null
  /** Lado do quadrado, em px. Default 28 (a escala da listagem). */
  size?: number
  className?: string
}

function initialOf(name: string): string {
  return name.trim().charAt(0).toUpperCase() || '—'
}

export function CustomerLogo({ name, src, size = 28, className }: CustomerLogoProps): ReactElement {
  const box: CSSProperties = { width: size, height: size }
  const shared = 'border-hair bg-sunk shrink-0 overflow-hidden rounded-md border'

  if (src) {
    return (
      <img
        src={src}
        // `alt` vazio + `aria-hidden`: o nome do cliente já está no <Link> ao
        // lado, e um leitor de tela que anunciasse "logotipo da B3, B3" leria
        // a mesma coisa duas vezes.
        alt=""
        aria-hidden="true"
        style={box}
        className={cn(shared, 'object-contain', className)}
      />
    )
  }

  return (
    <span
      aria-hidden="true"
      style={{ ...box, fontSize: Math.round(size * 0.42) }}
      className={cn(
        shared,
        'text-ink-2 inline-flex items-center justify-center font-medium',
        className,
      )}
    >
      {initialOf(name)}
    </span>
  )
}

export default CustomerLogo
